import React, { useState, useEffect } from 'react';
import {
  DocumentDuplicateIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentListIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../services/api';
import { useTranslation } from 'react-i18next';
import { marked } from 'marked';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';

interface ContractType {
  id: string;
  name: string;
  description: string;
  category: string;
  common_clauses: string[];
}

interface Country {
  code: string;
  name: string;
  legal_system: string;
}

interface State {
  code: string;
  name: string;
}

interface ClauseTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
}

interface GeneratedContract {
  contract_type: string;
  contract_name: string;
  jurisdiction: string;
  effective_date: string;
  party1: {
    name: string;
    type: string;
    address: string | null;
  };
  party2: {
    name: string;
    type: string;
    address: string | null;
  };
  contract_text: string;
  disclaimer: string;
}

interface ContractReview {
  contract_type: string;
  analysis: string;
  disclaimer: string;
}

const TABS = ['generate', 'review', 'clauses'] as const;
type TabType = typeof TABS[number];

export const BusinessContracts: React.FC = () => {
  const { t } = useTranslation();

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('generate');

  // Contract types and metadata
  const [contractTypes, setContractTypes] = useState<ContractType[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [clauseTemplates, setClauseTemplates] = useState<ClauseTemplate[]>([]);

  // Generate tab state
  const [selectedContractType, setSelectedContractType] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [selectedState, setSelectedState] = useState('');
  const [party1Name, setParty1Name] = useState('');
  const [party1Type, setParty1Type] = useState<'individual' | 'company'>('company');
  const [party1Address, setParty1Address] = useState('');
  const [party2Name, setParty2Name] = useState('');
  const [party2Type, setParty2Type] = useState<'individual' | 'company'>('individual');
  const [party2Address, setParty2Address] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [termLength, setTermLength] = useState('');
  const [additionalDetails, setAdditionalDetails] = useState<Record<string, string>>({});
  const [generatedContract, setGeneratedContract] = useState<GeneratedContract | null>(null);

  // Review tab state
  const [reviewContractType, setReviewContractType] = useState('');
  const [contractText, setContractText] = useState('');
  const [contractReview, setContractReview] = useState<ContractReview | null>(null);

  // Clauses tab state
  const [selectedClauseTemplate, setSelectedClauseTemplate] = useState('');
  const [clauseContext, setClauseContext] = useState('');
  const [clauseRequirements, setClauseRequirements] = useState('');
  const [generatedClause, setGeneratedClause] = useState<string | null>(null);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generateStep, setGenerateStep] = useState(0);

  // Load initial data
  useEffect(() => {
    loadContractTypes();
    loadCountries();
    loadClauseTemplates();
  }, []);

  // Load states when country changes
  useEffect(() => {
    if (selectedCountry) {
      loadStates(selectedCountry);
    }
  }, [selectedCountry]);

  const loadContractTypes = async () => {
    try {
      const response = await apiClient.get('/business-contracts/types');
      setContractTypes(response.data.contract_types);
    } catch (err: any) {
      console.error('Failed to load contract types:', err);
    }
  };

  const loadCountries = async () => {
    try {
      const response = await apiClient.get('/business-contracts/countries');
      setCountries(response.data.countries);
    } catch (err: any) {
      console.error('Failed to load countries:', err);
    }
  };

  const loadStates = async (country: string) => {
    try {
      const response = await apiClient.get(`/business-contracts/states/${country}`);
      setStates(response.data.states);
    } catch (err: any) {
      console.error('Failed to load states:', err);
      setStates([]);
    }
  };

  const loadClauseTemplates = async () => {
    try {
      const response = await apiClient.get('/business-contracts/clause-templates');
      setClauseTemplates(response.data.templates);
    } catch (err: any) {
      console.error('Failed to load clause templates:', err);
    }
  };

  const handleGenerateContract = async () => {
    if (!selectedContractType || !party1Name || !party2Name) {
      setError(t('businessContracts:errors.requiredFields'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/business-contracts/generate', {
        contract_type: selectedContractType,
        country: selectedCountry,
        state: selectedState || null,
        party1_name: party1Name,
        party1_type: party1Type,
        party1_address: party1Address || null,
        party2_name: party2Name,
        party2_type: party2Type,
        party2_address: party2Address || null,
        effective_date: effectiveDate || null,
        term_length: termLength || null,
        additional_details: Object.keys(additionalDetails).length > 0 ? additionalDetails : null,
      });

      setGeneratedContract(response.data);
      setGenerateStep(2);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('businessContracts:errors.generateFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleReviewContract = async () => {
    if (!contractText.trim()) {
      setError(t('businessContracts:errors.enterContractText'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/business-contracts/review', {
        contract_type: reviewContractType || 'general',
        contract_text: contractText,
      });

      setContractReview(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('businessContracts:errors.reviewFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateClause = async () => {
    if (!selectedClauseTemplate) {
      setError(t('businessContracts:errors.selectClauseType'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/business-contracts/customize-clause', null, {
        params: {
          clause_type: selectedClauseTemplate,
          context: clauseContext || null,
          requirements: clauseRequirements || null,
        },
      });

      setGeneratedClause(response.data.clause_text);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('businessContracts:errors.clauseFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = (content: string, title: string) => async (format: 'word' | 'pdf' | 'text') => {
    await exportDocument(format, content, title);
  };

  const handlePrint = (content: string, title: string) => () => {
    printDocument(content, title);
  };

  const printDocument = (content: string, title: string) => {
    if (!content) return;

    // Convert markdown to HTML using marked library
    const formattedContent = marked(content, { async: false }) as string;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title}</title>
          <style>
            body {
              font-family: 'Times New Roman', Georgia, serif;
              line-height: 1.6;
              color: #1f2937;
              max-width: 800px;
              margin: 0 auto;
              padding: 40px 20px;
            }
            h1 { font-size: 24px; font-weight: bold; margin-bottom: 8px; margin-top: 24px; text-align: center; }
            h2 { font-size: 20px; font-weight: bold; margin-top: 20px; margin-bottom: 12px; }
            h3 { font-size: 16px; font-weight: bold; margin-top: 16px; margin-bottom: 8px; text-transform: uppercase; }
            p { margin-bottom: 12px; line-height: 1.8; text-align: justify; }
            ul { margin-left: 24px; margin-bottom: 12px; list-style-type: disc; }
            ol { margin-left: 24px; margin-bottom: 12px; }
            li { margin-bottom: 4px; line-height: 1.6; }
            strong { font-weight: bold; }
            em { font-style: italic; }
            code { background-color: #f3f4f6; padding: 2px 6px; border-radius: 4px; font-family: monospace; }
            pre { background-color: #f3f4f6; padding: 16px; border-radius: 8px; overflow-x: auto; }
            blockquote { border-left: 4px solid #d1d5db; padding-left: 16px; margin-left: 0; color: #6b7280; }
            .header { margin-bottom: 32px; padding-bottom: 16px; border-bottom: 2px solid #d1d5db; text-align: center; }
            .document-title { font-size: 22px; font-weight: bold; color: #111827; text-transform: uppercase; letter-spacing: 1px; }
            .content { font-size: 12pt; }
            @media print {
              body { padding: 20px; font-size: 11pt; }
              h1 { font-size: 18pt; }
              h2 { font-size: 14pt; }
              h3 { font-size: 12pt; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="document-title">${title}</div>
          </div>
          <div class="content">
            ${formattedContent}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const exportDocument = async (format: 'word' | 'text' | 'pdf', content: string, title: string) => {
    if (!content) return;

    try {
      const extensionMap: Record<string, string> = { word: 'docx', pdf: 'pdf', text: 'txt' };
      const extension = extensionMap[format];
      const filename = `${title.replace(/[^a-zA-Z0-9]/g, '_')}.${extension}`;

      const response = await apiClient.post('/export/document', {
        content: content,
        title: title,
        format: extension
      }, {
        responseType: 'blob'
      });

      // Create blob from response
      const blob = response.data;

      // Try to use File System Access API (Chrome/Edge) for "Save As" dialog
      if ('showSaveFilePicker' in window) {
        try {
          const mimeTypes: Record<string, string> = {
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            pdf: 'application/pdf',
            txt: 'text/plain'
          };

          const descriptions: Record<string, string> = {
            word: 'Word Document',
            pdf: 'PDF Document',
            text: 'Text File'
          };

          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: descriptions[format],
              accept: { [mimeTypes[extension]]: [`.${extension}`] }
            }]
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return; // Success, exit function
        } catch (err: any) {
          // User cancelled or API not available, fall back to standard download
          if (err.name !== 'AbortError') {
            console.warn('File System Access API failed, falling back to standard download:', err);
          } else {
            // User cancelled the save dialog
            return;
          }
        }
      }

      // Fallback: Standard download (goes to Downloads folder)
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      setError(t('businessContracts:errors.exportFailed'));
    }
  };

  const resetGenerateForm = () => {
    setSelectedContractType('');
    setParty1Name('');
    setParty1Type('company');
    setParty1Address('');
    setParty2Name('');
    setParty2Type('individual');
    setParty2Address('');
    setEffectiveDate('');
    setTermLength('');
    setAdditionalDetails({});
    setGeneratedContract(null);
    setGenerateStep(0);
  };

  const getSelectedContractInfo = () => {
    return contractTypes.find(ct => ct.id === selectedContractType);
  };

  const renderTabs = () => (
    <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
      <nav className="-mb-px flex space-x-8">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === tab
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            {t(`businessContracts:tabs.${tab}`)}
          </button>
        ))}
      </nav>
    </div>
  );

  const renderGenerateTab = () => {
    const selectedContract = getSelectedContractInfo();

    if (generateStep === 2 && generatedContract) {
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {generatedContract.contract_name}
            </h3>
            <ExportDropdownMenu
              onExport={handleExport(generatedContract.contract_text, generatedContract.contract_name)}
              onPrint={handlePrint(generatedContract.contract_text, generatedContract.contract_name)}
              showPrint={true}
            />
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{t('businessContracts:generate.jurisdiction')}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{generatedContract.jurisdiction}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{t('businessContracts:generate.effectiveDate')}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{generatedContract.effective_date}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{t('businessContracts:generate.party1')}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{generatedContract.party1.name}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{t('businessContracts:generate.party2')}:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{generatedContract.party2.name}</span>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 font-mono">
              {generatedContract.contract_text}
            </pre>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-3 flex-shrink-0" />
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                {generatedContract.disclaimer}
              </p>
            </div>
          </div>

          <button
            onClick={resetGenerateForm}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            <ChevronLeftIcon className="h-4 w-4 mr-2" />
            {t('businessContracts:generate.createAnother')}
          </button>
        </div>
      );
    }

    if (generateStep === 1) {
      return (
        <div className="space-y-6">
          <div className="flex items-center mb-4">
            <button
              onClick={() => setGenerateStep(0)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mr-2"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {selectedContract?.name} - {t('businessContracts:generate.enterDetails')}
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Jurisdiction */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('businessContracts:generate.country')}
              </label>
              <select
                value={selectedCountry}
                onChange={(e) => {
                  setSelectedCountry(e.target.value);
                  setSelectedState('');
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              >
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            {selectedCountry === 'US' && states.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('businessContracts:generate.state')}
                </label>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">{t('businessContracts:generate.selectState')}</option>
                  {states.map((state) => (
                    <option key={state.code} value={state.code}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Party 1 */}
            <div className="md:col-span-2">
              <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                {t('businessContracts:generate.party1Details')}
              </h4>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('businessContracts:generate.party1Name')} *
              </label>
              <input
                type="text"
                value={party1Name}
                onChange={(e) => setParty1Name(e.target.value)}
                placeholder={t('businessContracts:generate.party1NamePlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('businessContracts:generate.party1Type')}
              </label>
              <select
                value={party1Type}
                onChange={(e) => setParty1Type(e.target.value as 'individual' | 'company')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="company">{t('businessContracts:generate.company')}</option>
                <option value="individual">{t('businessContracts:generate.individual')}</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('businessContracts:generate.party1Address')}
              </label>
              <input
                type="text"
                value={party1Address}
                onChange={(e) => setParty1Address(e.target.value)}
                placeholder={t('businessContracts:generate.addressPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Party 2 */}
            <div className="md:col-span-2">
              <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3 mt-4">
                {t('businessContracts:generate.party2Details')}
              </h4>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('businessContracts:generate.party2Name')} *
              </label>
              <input
                type="text"
                value={party2Name}
                onChange={(e) => setParty2Name(e.target.value)}
                placeholder={t('businessContracts:generate.party2NamePlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('businessContracts:generate.party2Type')}
              </label>
              <select
                value={party2Type}
                onChange={(e) => setParty2Type(e.target.value as 'individual' | 'company')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="individual">{t('businessContracts:generate.individual')}</option>
                <option value="company">{t('businessContracts:generate.company')}</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('businessContracts:generate.party2Address')}
              </label>
              <input
                type="text"
                value={party2Address}
                onChange={(e) => setParty2Address(e.target.value)}
                placeholder={t('businessContracts:generate.addressPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Additional Details */}
            <div className="md:col-span-2">
              <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3 mt-4">
                {t('businessContracts:generate.additionalDetails')}
              </h4>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('businessContracts:generate.effectiveDate')}
              </label>
              <input
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('businessContracts:generate.termLength')}
              </label>
              <input
                type="text"
                value={termLength}
                onChange={(e) => setTermLength(e.target.value)}
                placeholder={t('businessContracts:generate.termLengthPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setGenerateStep(0)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              {t('common:buttons.cancel')}
            </button>
            <button
              onClick={handleGenerateContract}
              disabled={isLoading || !party1Name || !party2Name}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <ArrowPathIcon className="animate-spin h-4 w-4 mr-2" />
                  {t('businessContracts:generate.generating')}
                </>
              ) : (
                <>
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  {t('businessContracts:generate.generateContract')}
                </>
              )}
            </button>
          </div>
        </div>
      );
    }

    // Step 0: Select contract type
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          {t('businessContracts:generate.selectContractType')}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {contractTypes.map((contract) => (
            <button
              key={contract.id}
              onClick={() => {
                setSelectedContractType(contract.id);
                setGenerateStep(1);
              }}
              className={`text-left p-4 border rounded-lg transition-colors ${
                selectedContractType === contract.id
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600'
              }`}
            >
              <h4 className="font-medium text-gray-900 dark:text-white">
                {contract.name}
              </h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {contract.description}
              </p>
              <div className="mt-2 flex flex-wrap gap-1">
                {contract.common_clauses.slice(0, 3).map((clause, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                  >
                    {clause}
                  </span>
                ))}
                {contract.common_clauses.length > 3 && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    +{contract.common_clauses.length - 3} more
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderReviewTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('businessContracts:review.contractType')}
          </label>
          <select
            value={reviewContractType}
            onChange={(e) => setReviewContractType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="">{t('businessContracts:review.selectType')}</option>
            {contractTypes.map((contract) => (
              <option key={contract.id} value={contract.id}>
                {contract.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('businessContracts:review.contractText')} *
        </label>
        <textarea
          value={contractText}
          onChange={(e) => setContractText(e.target.value)}
          rows={12}
          placeholder={t('businessContracts:review.pasteContract')}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white font-mono text-sm"
        />
      </div>

      <button
        onClick={handleReviewContract}
        disabled={isLoading || !contractText.trim()}
        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <>
            <ArrowPathIcon className="animate-spin h-4 w-4 mr-2" />
            {t('businessContracts:review.analyzing')}
          </>
        ) : (
          <>
            <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
            {t('businessContracts:review.analyzeContract')}
          </>
        )}
      </button>

      {contractReview && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {t('businessContracts:review.analysisResults')}
            </h3>
            <ExportDropdownMenu
              onExport={handleExport(contractReview.analysis, 'Contract Analysis')}
              onPrint={handlePrint(contractReview.analysis, 'Contract Analysis')}
              showPrint={true}
            />
          </div>

          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <div className="prose dark:prose-invert max-w-none">
              <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200">
                {contractReview.analysis}
              </pre>
            </div>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-3 flex-shrink-0" />
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                {contractReview.disclaimer}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderClausesTab = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
        {t('businessContracts:clauses.title')}
      </h3>

      <p className="text-sm text-gray-600 dark:text-gray-400">
        {t('businessContracts:clauses.description')}
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('businessContracts:clauses.selectType')} *
          </label>
          <select
            value={selectedClauseTemplate}
            onChange={(e) => setSelectedClauseTemplate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="">{t('businessContracts:clauses.choosePlaceholder')}</option>
            {clauseTemplates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('businessContracts:clauses.context')}
          </label>
          <input
            type="text"
            value={clauseContext}
            onChange={(e) => setClauseContext(e.target.value)}
            placeholder={t('businessContracts:clauses.contextPlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div className="lg:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('businessContracts:clauses.requirements')}
          </label>
          <textarea
            value={clauseRequirements}
            onChange={(e) => setClauseRequirements(e.target.value)}
            rows={3}
            placeholder={t('businessContracts:clauses.requirementsPlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      <button
        onClick={handleGenerateClause}
        disabled={isLoading || !selectedClauseTemplate}
        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <>
            <ArrowPathIcon className="animate-spin h-4 w-4 mr-2" />
            {t('businessContracts:clauses.generating')}
          </>
        ) : (
          <>
            <PlusIcon className="h-4 w-4 mr-2" />
            {t('businessContracts:clauses.generateClause')}
          </>
        )}
      </button>

      {generatedClause && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-md font-medium text-gray-900 dark:text-white">
              {t('businessContracts:clauses.generatedClause')}
            </h4>
            <div className="flex space-x-2">
              <button
                onClick={() => navigator.clipboard.writeText(generatedClause)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <ClipboardDocumentListIcon className="h-4 w-4 mr-2" />
                {t('common:buttons.copy')}
              </button>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200">
              {generatedClause}
            </pre>
          </div>
        </div>
      )}

      {/* Clause Templates Reference */}
      <div className="mt-8">
        <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
          {t('businessContracts:clauses.availableTemplates')}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {clauseTemplates.map((template) => (
            <div
              key={template.id}
              className="p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
            >
              <h5 className="font-medium text-gray-900 dark:text-white text-sm">
                {template.name}
              </h5>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {template.description}
              </p>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 mt-2">
                {template.category}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center space-x-3">
          <DocumentDuplicateIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('businessContracts:title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {t('businessContracts:subtitle')}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-3" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="mt-2 text-sm text-red-600 dark:text-red-400 hover:underline"
          >
            {t('common:buttons.close')}
          </button>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        {renderTabs()}

        {activeTab === 'generate' && renderGenerateTab()}
        {activeTab === 'review' && renderReviewTab()}
        {activeTab === 'clauses' && renderClausesTab()}
      </div>
    </div>
  );
};

export default BusinessContracts;
